/**
 * **„Zmień cel dzienny", typed by hand** — the target sheet's fields, the split, and the request
 * (nutrition R2). The calculator half of the mockup is `nutritionCalculator` (R19); it only fills these.
 *
 * Energy is required unless all three macros are given. A typed energy is sent and stored as typed,
 * even when it disagrees with 4/4/9. Only an EMPTY energy with all three macros is derived, and then
 * by the SERVER: the request leaves it out, so the figure the sheet shows and the one stored cannot
 * be computed by two different hands. A stored target whose energy IS its macros' 4/4/9 reopens with
 * energy empty, so editing a macro cannot send the old figure back as typed. The bounds are R2's and are checked here only to name the
 * field before the round trip; the server checks them again and its refusal is mapped to the same
 * words.
 */

import { NUTRIENTS, type Nutrient, type NutritionTargetValues } from './nutrition';

export type TargetField = Nutrient;
export type TargetFormValues = Readonly<Record<TargetField, string>>;

/** R2's inclusive bounds, whole units. */
export const TARGET_BOUNDS: Readonly<Record<TargetField, readonly [number, number]>> = {
  energy: [800, 8000],
  protein: [1, 500],
  carbs: [0, 1000],
  fat: [0, 1000],
};

/**
 * A keystroke that would take a field above its R2 maximum is refused: the field keeps `previous`.
 * Minimums wait for the save, so a value can be typed through them.
 */
export function capTargetInput(field: TargetField, previous: string, next: string): string {
  const value = parseWhole(next);
  return typeof value === 'number' && value > TARGET_BOUNDS[field][1] ? previous : next;
}

/** kcal per gram — R2's own derivation. */
const KCAL_PER_GRAM: Readonly<Record<Exclude<TargetField, 'energy'>, number>> = {
  protein: 4,
  carbs: 4,
  fat: 9,
};

/** The energy of the three macros by 4/4/9. */
export function macroKcal(protein: number, carbs: number, fat: number): number {
  return protein * KCAL_PER_GRAM.protein + carbs * KCAL_PER_GRAM.carbs + fat * KCAL_PER_GRAM.fat;
}

export const EMPTY_TARGET_FORM: TargetFormValues = { energy: '', protein: '', carbs: '', fat: '' };

export function formFromTarget(target: NutritionTargetValues | null): TargetFormValues {
  if (target === null) return EMPTY_TARGET_FORM;
  const text = (value: number | null) => (value === null ? '' : String(value));
  const { protein, carbs, fat } = target;
  const derived =
    protein !== null &&
    carbs !== null &&
    fat !== null &&
    target.energy === macroKcal(protein, carbs, fat);
  return {
    energy: derived ? '' : String(target.energy),
    protein: text(target.protein),
    carbs: text(target.carbs),
    fat: text(target.fat),
  };
}

/** `''` is "no value"; digits (spaces allowed, „2 200") are a whole number; anything else is not. */
export function parseWhole(text: string): number | null | 'invalid' {
  const compact = text.replace(/[\s\u00a0]/g, '');
  if (compact === '') return null;
  // A negative whole number is whole: the bounds refuse it as out of range, not as „not whole".
  return /^-?\d+$/.test(compact) ? Number(compact) : 'invalid';
}

export interface TargetSplitPart {
  readonly nutrient: Exclude<TargetField, 'energy'>;
  /** Share of the macros' energy, 0–1, for the bar's flex. */
  readonly share: number;
  readonly percent: number;
}

export interface TargetFormView {
  /** 4/4/9 of the three macros when all are typed, whatever the energy field holds. */
  readonly macroEnergy: number | null;
  /** The energy the server will derive: `macroEnergy` while the energy field is empty. */
  readonly derivedEnergy: number | null;
  /** The macros alone add up past R2's energy maximum while the energy field is empty. */
  readonly energyOverCap: boolean;
  /** Neither energy typed nor all three macros: nothing to save as the target's energy. */
  readonly energyMissing: boolean;
  readonly split: readonly TargetSplitPart[] | null;
}

function macroGrams(values: TargetFormValues): Record<'protein' | 'carbs' | 'fat', number> | null {
  const protein = parseWhole(values.protein);
  const carbs = parseWhole(values.carbs);
  const fat = parseWhole(values.fat);
  if (typeof protein !== 'number' || typeof carbs !== 'number' || typeof fat !== 'number') {
    return null;
  }
  if (protein < 0 || carbs < 0 || fat < 0) return null;
  return { protein, carbs, fat };
}

export function targetFormView(values: TargetFormValues): TargetFormView {
  const grams = macroGrams(values);
  if (grams === null) {
    return {
      macroEnergy: null,
      derivedEnergy: null,
      energyOverCap: false,
      energyMissing: parseWhole(values.energy) === null,
      split: null,
    };
  }
  const parts = (['protein', 'carbs', 'fat'] as const).map((nutrient) => ({
    nutrient,
    kcal: grams[nutrient] * KCAL_PER_GRAM[nutrient],
  }));
  const total = macroKcal(grams.protein, grams.carbs, grams.fat);
  const derivedEnergy = parseWhole(values.energy) === null ? total : null;
  return {
    macroEnergy: total,
    derivedEnergy,
    energyOverCap: derivedEnergy !== null && derivedEnergy > TARGET_BOUNDS.energy[1],
    energyMissing: false,
    split:
      total === 0
        ? null
        : parts.map((part) => ({
            nutrient: part.nutrient,
            share: part.kcal / total,
            percent: Math.round((part.kcal / total) * 100),
          })),
  };
}

export type TargetFieldError = 'notWhole' | 'outOfRange';

export interface TargetRequest {
  readonly energyKcal?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
}

export interface TargetSubmission {
  readonly request: TargetRequest | null;
  readonly fieldErrors: Readonly<Partial<Record<TargetField, TargetFieldError>>>;
  /** Neither energy nor all three macros. */
  readonly energyRequired: boolean;
  /** Told at the energy field and fixed in the macros, so it is not an energy field error. */
  readonly energyOverCap: boolean;
}

export function buildTargetRequest(values: TargetFormValues): TargetSubmission {
  const fieldErrors: Partial<Record<TargetField, TargetFieldError>> = {};
  const parsed: Partial<Record<TargetField, number>> = {};
  const { derivedEnergy: derived, energyOverCap } = targetFormView(values);

  for (const field of NUTRIENTS) {
    const value = parseWhole(values[field]);
    if (value === 'invalid') {
      fieldErrors[field] = 'notWhole';
      continue;
    }
    if (value === null) continue;
    const [low, high] = TARGET_BOUNDS[field];
    if (value < low || value > high) fieldErrors[field] = 'outOfRange';
    else parsed[field] = value;
  }
  if (derived !== null && derived < TARGET_BOUNDS.energy[0]) fieldErrors.energy = 'outOfRange';

  const energyRequired =
    derived === null && parsed.energy === undefined && fieldErrors.energy === undefined;
  if (energyRequired || energyOverCap || Object.keys(fieldErrors).length > 0) {
    return { request: null, fieldErrors, energyRequired, energyOverCap };
  }
  return {
    request: {
      ...(parsed.energy === undefined ? {} : { energyKcal: parsed.energy }),
      ...(parsed.protein === undefined ? {} : { proteinG: parsed.protein }),
      ...(parsed.carbs === undefined ? {} : { carbsG: parsed.carbs }),
      ...(parsed.fat === undefined ? {} : { fatG: parsed.fat }),
    },
    fieldErrors,
    energyRequired: false,
    energyOverCap: false,
  };
}

/**
 * The request would store the current target unchanged: the same energy (typed, or the server's
 * 4/4/9 of all three macros) and the same three macros, nulls included. Saving it would only add a
 * version and move „Cel dzienny od" to today.
 */
export function requestMatchesTarget(
  request: TargetRequest,
  target: NutritionTargetValues | null,
): boolean {
  if (target === null) return false;
  const protein = request.proteinG ?? null;
  const carbs = request.carbsG ?? null;
  const fat = request.fatG ?? null;
  const energy =
    request.energyKcal ??
    (protein === null || carbs === null || fat === null ? null : macroKcal(protein, carbs, fat));
  return (
    energy === target.energy &&
    protein === target.protein &&
    carbs === target.carbs &&
    fat === target.fat
  );
}

/** The sentence a refused save is told in, by the backend's stable code. */
export function targetSaveErrorKey(code: string | undefined): string {
  switch (code) {
    case 'nutrition_unavailable':
      return 'web:clientDetail.nutrition.errors.unavailable';
    case 'nutrition_target_trainer_only':
      return 'web:clientDetail.nutrition.errors.trainerOnly';
    case 'nutrition_value_out_of_range':
      return 'web:clientDetail.nutrition.errors.outOfRange';
    case 'nutrition_energy_required':
      return 'web:clientDetail.nutrition.errors.energyRequired';
    default:
      return 'web:clientDetail.nutrition.errors.saveFailed';
  }
}
